import React, { CSSProperties } from 'react';
import { Tree, NodeApi } from 'react-arborist';
import { useApolloClient, gql } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { Station, Stop } from '../types';

type TrackNode = { [id: string]: TrackNode[] };

interface TreeNodeData {
    id: string;
    label: string;
    status: string;
    asynchronousChildren?: boolean;
    isAsyncChild?: boolean;
    children?: TreeNodeData[];
}

function useBuildTree(track: TrackNode, stops: Stop[]): TreeNodeData[] {
    const client = useApolloClient();
    const stopMap = new Map(stops.map((s) => [s.id, s]));

    const getStationName = (stationId: string): string => {
        try {
            const data = client.readFragment({
                id: `Station:${stationId}`,
                fragment: gql`
                    fragment StationName on Station {
                        name
                    }
                `
            });
            return data?.name || '(Unnamed)';
        } catch {
            return '(Unknown)';
        }
    };

    const build = (node: TrackNode, parentHasAsyncChildren = false): TreeNodeData[] =>
        Object.entries(node)
            .filter(([id]) => id && id.trim() !== '') // Filter out empty or whitespace-only keys
            .map(([id, children]) => {
                const stop = stopMap.get(id);
                // Use stop.name first, then try to get station name from cache, fallback to id
                const label = stop?.name || (stop?.type === 'station' && stop.stationId ? getStationName(stop.stationId) : id);
                const hasAsyncChildren = stop?.asynchronousChildren || false;
                return {
                    id: id,
                    label,
                    status: stop?.status || '',
                    asynchronousChildren: hasAsyncChildren,
                    isAsyncChild: parentHasAsyncChildren,
                    children: children.flatMap((childNode) => build(childNode, hasAsyncChildren))
                };
            });

    // Return empty array if track is empty or invalid
    if (!track || Object.keys(track).length === 0) {
        return [];
    }
    return build(track);
}

export default function TrackTree({
    track,
    stops,
    allStations,
    height
}: {
    track: TrackNode;
    stops: Stop[];
    allStations: Station[] | undefined;
    height?: number;
}) {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const data = useBuildTree(track, stops);
    const nodeIndent = isMobile ? 12 : 20;

    // Calculate dynamic height based on number of stops (each node roughly 32px)
    const calculateHeight = () => {
        if (height !== undefined) return height;
        const nodeHeight = 32;
        return stops.length * nodeHeight;
    };

    const getStatusColor = (status: string): string => {
        if (isDarkMode) {
            // Dark theme colors using theme palette
            switch (status) {
                case 'currentlyStationed':
                    return theme.palette.warning.dark; // Dark yellow/amber
                case 'completed':
                    return theme.palette.success.dark; // Dark green
                default:
                    return theme.palette.grey[700]; // Darker gray from theme
            }
        } else {
            // Light theme colors using theme palette
            switch (status) {
                case 'currentlyStationed':
                    return theme.palette.warning.light;
                case 'completed':
                    return theme.palette.success.light;
                default:
                    return theme.palette.grey[200];
            }
        }
    };

    const getBorderColor = (status: string): string => {
        if (isDarkMode) {
            switch (status) {
                case 'currentlyStationed':
                case 'currently_stationed':
                    return theme.palette.warning.light;
                case 'completed':
                    return theme.palette.success.light;
                default:
                    return theme.palette.grey[400];
            }
        } else {
            switch (status) {
                case 'currentlyStationed':
                case 'currently_stationed':
                    return theme.palette.warning.dark;
                case 'completed':
                    return theme.palette.success.dark;
                default:
                    return theme.palette.grey[600];
            }
        }
    };

    return (
        <Tree<TreeNodeData>
            data={data}
            openByDefault
            childrenAccessor="children"
            selectionFollowsFocus
            height={calculateHeight()}
            width={isMobile ? 250 : 400}
        >
            {({ node, style }: { node: NodeApi<TreeNodeData>; style: CSSProperties }) => (
                <div
                    style={{
                        ...style,
                        backgroundColor: getStatusColor(node.data.status),
                        color: isDarkMode ? theme.palette.common.white : 'inherit',
                        padding: isMobile ? '3px 6px' : '4px 8px',
                        borderRadius: 4,
                        fontFamily: 'Arial, sans-serif',
                        fontSize: isMobile ? 12 : 14,
                        marginLeft: node.level * nodeIndent,
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? 2 : 4,
                        ...(node.data.isAsyncChild && {
                            borderLeft: `4px solid ${getBorderColor(node.data.status)}`,
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0
                        })
                    }}
                >
                    {node.data.asynchronousChildren && (
                        <CallSplitIcon
                            fontSize="small"
                            style={{
                                transform: 'rotate(180deg)',
                                color: isDarkMode ? theme.palette.common.white : theme.palette.grey[800],
                                backgroundColor: isDarkMode ? theme.palette.grey[700] : theme.palette.grey[300],
                                borderRadius: '50%',
                                padding: '2px',
                                flexShrink: 0
                            }}
                            titleAccess="Child Stations will run in Parallel"
                        />
                    )}
                    {node.data.label}
                </div>
            )}
        </Tree>
    );
}
